import { Container } from "@/types/v2";
import { Fragment, useMemo } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/Base/Tooltip";
import TimeDisplay from "@/components/Common/TimeDisplay";
import DeltaDisplay from "@/components/Common/DeltaDisplay";
import { useContainerMetrics } from "@/hooks/useContainerMetrics";
import { container as containerRepository } from "@/repositories/container";
import { useFeatures } from "@/hooks/useFeatures";
import { JobHourDisplay } from "@/components/Dashboard/components/Card/JobHourDisplay";
import { useStore } from "@/stores/useStore";

export type ContainerFooterProps = {
  container?: Container;
};
export const ContainerFooter = ({ container }: ContainerFooterProps) => {
  const { shop, cards } = useStore();

  const {
    features: { isCardLaborEnabled, isManualCardOnlyEnabled },
  } = useFeatures(shop);

  const isManualCardOnlyLaborEnabled = useMemo(() => {
    if (!isManualCardOnlyEnabled) return false;
    if (!isCardLaborEnabled) return false;
    return shop?.cardConfig?.cardContents?.manualJobHoursEnabled ?? false;
  }, [isManualCardOnlyEnabled, shop, isCardLaborEnabled]);
  const displayName = useMemo(
    () => container?.displayName ?? container?.id ?? "Container",
    [container]
  );

  const containerHours = container
    ? containerRepository.calculateContainerHours(container, cards ?? [])
    : { totalApprovedHours: 0, totalCompletedHours: 0 };

  if (!container)
    return (
      <div className="sticky top-0 z-10 flex w-full flex-row items-center justify-start p-1">
        <p>{displayName}</p>
      </div>
    );

  return (
    <div className="flex w-full flex-row items-center justify-start gap-2 border-t border-slate-900 pt-1 text-xs text-gray-400">
      {isCardLaborEnabled ? (
        <Fragment>
          <JobHourDisplay
            totalLaborHours={{
              estimatedLabor: containerHours.totalApprovedHours,
              loggedLabor: containerHours.totalCompletedHours,
            }}
            isManualCardOnly={isManualCardOnlyEnabled}
            isManualJobHoursEnabled={isManualCardOnlyLaborEnabled}
          />
        </Fragment>
      ) : null}
      <div className="flex-grow" />

      <ContainerMetrics containerId={container.id} />
    </div>
  );
};

const ContainerMetrics = ({ containerId }: { containerId: string }) => {
  const { averageTimeInContainer, weekAverage, deltaDisplayMode } =
    useContainerMetrics(containerId);

  if (averageTimeInContainer === 0 && weekAverage === 0) return null;
  return (
    <div className={"flex flex-row gap-2"}>
      <Tooltip>
        <TooltipTrigger>
          <TimeDisplay
            size={"xs"}
            timeInSeconds={Math.max(averageTimeInContainer, 0)}
          />
        </TooltipTrigger>
        <TooltipContent>Average time in container today</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger>
          <DeltaDisplay
            delta={Math.max(
              Math.round((averageTimeInContainer - weekAverage) / 60),
              0
            )}
            units={"m"}
            size={"xs"}
          >
            <TimeDisplay size={"xs"} timeInSeconds={weekAverage} disabled />
          </DeltaDisplay>
        </TooltipTrigger>
        <TooltipContent>
          {deltaDisplayMode === 0
            ? "Average time in container the past week"
            : "Change in average time compared to the past week"}
        </TooltipContent>
      </Tooltip>
    </div>
  );
};
